package dev.akshaychordiya.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div

/**
 * A `div` with a background image rather than an `<img>`, which is not a stylistic preference.
 *
 * `ProseStyles` carries a `cssRule("img")` that sets `border-radius: 0.6rem`, `height: auto` and vertical margins on
 * every image in the prose column. As a descendant selector it outranks any single class we could put on an `<img>`
 * here, so the portrait rendered as a rounded square - which also un-cropped the conference logo in the corner of the
 * photo that the circle exists to hide.
 *
 * Narrowing the prose rule was the obvious alternative and it doesn't work: Kobweb renders Markdown images through
 * Silk, so all 32 images in the articles carry a class too. Any selector sharp enough to exclude this portrait either
 * excludes those as well or has to name a Silk-internal class. A background image sidesteps the whole fight - no
 * `<img>`, nothing for the prose rule to match.
 *
 * The trade-off is that a background image is invisible to assistive tech and to the browser's preload scanner, hence
 * the explicit `role="img"` and label below. Fine for one small avatar; don't reach for this pattern for content
 * images.
 */
val PortraitStyle = CssStyle.base {
    Modifier
        .size(clamp(8.cssRem, 30.vw, 11.cssRem))
        .borderRadius(50.percent)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .margin(bottom = 1.75.cssRem)
        .styleModifier {
            property("background-image", "url('/images/akshay.webp')")
            property("background-size", "cover")
            property("background-position", "center")
            property("background-repeat", "no-repeat")
        }
}

/**
 * The portrait, as a widget so Markdown pages can place it.
 *
 * Kobweb can call a composable from Markdown with `{{{ .components.widgets.Portrait }}}`, which is how `About.md`
 * gets a properly sized circular photo - a plain Markdown image would run the full width of the column.
 */
@Composable
fun Portrait() {
    Div(
        PortraitStyle.toModifier()
            .fadeIn()
            .attrsModifier {
                attr("role", "img")
                attr("aria-label", "Akshay Chordiya speaking at Android Makers by droidcon")
            }
            .toAttrs()
    )
}
